<script setup lang="ts">
import { computed, nextTick, onBeforeMount, ref, watch } from 'vue';
import { useDisplay } from 'vuetify';
import { VDataTable } from 'vuetify/components';

import {
  useDefaultGroupListRouteQuery,
  useDefaultUserListRouteQuery,
  useSelectedEntityRouteQuery,
} from '../composables/default';
import { Filters } from './table/types';
import { modals } from '../composables/useModal';
import { useLocale } from '../composables/useLocale';
import useMainTableFilters from './table/filters/useMainTableFilters';
import { SideBarEntityType, useSideBar } from '../composables/useSideBar';

import MainTableFooterPanel from './MainTableFooterPanel.vue';
import FiltersModal from './table/filters/FiltersModal.vue';
import type { FetchItems, SortOrder } from '@ilteducation/components';
import { ScrollingDataTableServer } from '@ilteducation/components';

const { t } = useLocale();
const { xs } = useDisplay();
const { openSideBar, closeSideBar } = useSideBar();
const { param: groupListQueryParams, setValue: setGroupListQueryParams } = useDefaultGroupListRouteQuery();
const { param: userListQueryParams, setValue: setUserListQueryParams } = useDefaultUserListRouteQuery();
const { param: selectedEntityUid } = useSelectedEntityRouteQuery();

type DataTableItem = Required<VDataTable['$props']>['items'][number];

const props = withDefaults(
  defineProps<{
    list: 'users' | 'groups';
    itemKey: string;
    filters: Filters;
    itemsPerPage: number;
    organizationUid: string;
    headers: VDataTable['$props']['headers'];
    fetchItems: FetchItems<(...args: any) => Promise<any>>;
    sortBy?: string;
    sortOrder?: SortOrder;
    searchString?: string;
    mobileHeaders?: VDataTable['$props']['headers'];
    selectStrategy?: VDataTable['$props']['selectStrategy'];
    enableFooterPanel?: boolean;
  }>(),
  {
    itemKey: 'uid',
    itemsPerPage: 50,
    selectStrategy: 'single',
    enableFooterPanel: false,
  },
);

const { filters: filtersFromQuery, setAvailableFilters, cleanUpOrSetDefaultFilters } = useMainTableFilters();

onBeforeMount(() => {
  setAvailableFilters(props.filters);
  // clear filter not in available filters, when list changes or initial mount
  nextTick(() => {
    cleanUpOrSetDefaultFilters();
  });
});

// the available filters can change while mounted, e.g. the role options depend on the selected organization
watch(
  () => props.filters,
  (newFilters) => {
    setAvailableFilters(newFilters);
    nextTick(() => {
      cleanUpOrSetDefaultFilters();
    });
  },
);

const isUserList = computed(() => props.list === 'users');
const isGroupList = computed(() => props.list === 'groups');

// Names the checkbox after the row it selects: on its own it is an unlabelled box in a column
// with no heading, so it reads as one of a stack of identical controls.
const selectRowLabel = (item: unknown) => {
  const { fullname, name } = item as { fullname?: string; name?: string };
  const rowName = isUserList.value ? fullname : name;
  return rowName ? `${t('lcp.select')}: ${rowName}` : t('lcp.select');
};

const table = ref<typeof ScrollingDataTableServer | null>(null);
const selectedTableItemUids = defineModel<string[]>({ default: [] });

const selectedItem = computed(() => {
  return (table?.value?.dataTable.items as any)?.find((i: any) => i.uid === selectedEntityUid.value) ?? undefined;
});

const sidebarEntity = computed(() => {
  const entity = isUserList.value ? 'user' : isGroupList.value ? 'group' : undefined;
  if (entity && selectedTableItemUids.value.length > 1) {
    return `${entity}-multiple` satisfies SideBarEntityType | undefined;
  }

  return entity satisfies SideBarEntityType | undefined;
});

const updateSorting = (sortBy: VDataTable['$props']['sortBy']) => {
  const sortbyObject = sortBy && sortBy.length > 0 ? sortBy[0] : { key: undefined, order: undefined };
  if (isGroupList.value) {
    setGroupListQueryParams({
      ...groupListQueryParams.value,
      sortBy: sortbyObject.key,
      sortOrder: typeof sortbyObject.order === 'string' ? (sortbyObject.order as SortOrder) : undefined,
    });
  }

  if (isUserList.value) {
    setUserListQueryParams({
      ...userListQueryParams.value,
      sortBy: sortbyObject.key,
      sortOrder: typeof sortbyObject.order === 'string' ? (sortbyObject.order as SortOrder) : undefined,
    });
  }
};

const updatePage = (page: number) => {
  if (page === 1) {
    if (isGroupList.value) {
      setGroupListQueryParams({ ...groupListQueryParams.value, page: undefined });
    }
    if (isUserList.value) {
      setUserListQueryParams({ ...userListQueryParams.value, page: undefined });
    }
    return;
  }

  if (isGroupList.value) {
    setGroupListQueryParams({ ...groupListQueryParams.value, page });
  }
  if (isUserList.value) {
    setUserListQueryParams({ ...userListQueryParams.value, page });
  }
};

// selection control
const rowClicked = (e: any, item: DataTableItem) => {
  const itemUid = item.item[props.itemKey];
  const isSelected = selectedTableItemUids.value.includes(itemUid);
  if (isSelected) {
    item.toggleSelect(item.internalItem);
  } else {
    if (props.selectStrategy !== 'single') {
      item.toggleSelect(item.internalItem);
    } else {
      selectedTableItemUids.value = [itemUid];
    }
  }
};

const headers = computed(() => {
  if (xs.value) {
    return props.mobileHeaders ?? props.headers;
  }
  return props.headers;
});

// footer panel controls
const isFooterPanelOpen = ref(false);
const openFooterPanel = () => {
  isFooterPanelOpen.value = true;
};

const closeFooterPanel = () => {
  isFooterPanelOpen.value = false;
};

const footerPanelAction = () => {
  if (sidebarEntity.value) {
    openSideBar(sidebarEntity.value, selectedEntityUid.value);
  }
};

const footerPanelTitle = computed(() => {
  if (selectedTableItemUids.value.length === 1) {
    if (isUserList.value) {
      return selectedItem.value?.fullname || selectedEntityUid.value;
    }

    if (isGroupList.value) {
      return selectedItem.value?.name || selectedEntityUid.value;
    }

    return selectedEntityUid.value;
  }

  if (selectedTableItemUids.value.length > 1) {
    if (isUserList.value) {
      return t('lcp.users_selected_count', { count: selectedTableItemUids.value.length });
    }
    if (isGroupList.value) {
      return t('lcp.groups_selected_count', { count: selectedTableItemUids.value.length });
    }
  }

  return '';
});

const footerPanelActionLabel = computed(() => {
  if (selectedTableItemUids.value.length === 1) {
    return t('lcp.to_profile');
  }
  if (selectedTableItemUids.value.length > 1) {
    return t('lcp.select_action');
  }

  return '';
});

const filtersFromQueryCleaned = computed(() => {
  if (!filtersFromQuery.value) {
    return {};
  }

  return filtersFromQuery.value;
});

watch(
  selectedTableItemUids,
  () => {
    if (selectedTableItemUids.value.length > 0) {
      if (props.enableFooterPanel) {
        openFooterPanel();
      } else {
        openSideBar(sidebarEntity.value, selectedEntityUid.value);
      }
    } else {
      closeFooterPanel();
      closeSideBar();
    }
  },
  { immediate: true },
);

defineExpose({
  reloadItems: async () => {
    await nextTick();
    table?.value?.reloadItems();
  },
});
</script>

<template>
  <ScrollingDataTableServer
    ref="table"
    v-model="selectedTableItemUids"
    :organizationUid="organizationUid"
    :filters="filtersFromQueryCleaned"
    :headers="headers"
    :items-per-page="itemsPerPage"
    :sort-by="sortBy"
    :sort-order="sortOrder"
    :search-string="searchString"
    :item-value="itemKey"
    :fetch-items="fetchItems"
    :loading-text="t('lcp.data_table_loading_please_wait')"
    :no-data-text="t('lcp.data_table_no_data')"
    :select-strategy="selectStrategy"
    @click:row="rowClicked"
    @update:sort-by="updateSorting"
    @update:page="updatePage"
    v-bind="$attrs"
    height="240"
  >
    <template v-if="enableFooterPanel" #bottom>
      <MainTableFooterPanel
        :is-open="isFooterPanelOpen"
        :title="footerPanelTitle"
        :action-label="footerPanelActionLabel"
        :action="footerPanelAction"
      />
    </template>
    <!-- @vue-ignore -->
    <template v-for="(_, slotName) in $slots" #[slotName]="slotProps" :key="slotName">
      <slot :name="slotName" v-bind="slotProps"></slot>
    </template>
    <!-- The select-all box is dropped from this column's header, and an empty header cell leaves
    every checkbox under it without a column to belong to. The heading is there for assistive
    technology only, so the column still reads as bare as it looks. -->
    <template #[`header.data-table-select`]>
      <span class="d-sr-only">{{ t('lcp.select') }}</span>
    </template>
    <!-- Bound from the slot's own props so the checkbox keeps the behaviour of the default one —
    including the click that must not reach the row underneath — and only gains a name. -->
    <template #[`item.data-table-select`]="{ props: selectProps, item }">
      <v-checkbox-btn v-bind="selectProps" :aria-label="selectRowLabel(item)" />
    </template>
    <template v-if="Object.keys(filters).length" #top>
      <FiltersModal :name="modals.MAIN_TABLE_FILTERS" :is-loading="false" :filters="filters" />
    </template>
  </ScrollingDataTableServer>
</template>

<style scoped lang="scss">
:deep(.selected-item) {
  background-color: rgb(var(--v-theme-primary-container));
}
</style>
